import React, { useRef } from 'react';
import { useAdoptedStyleSheet } from './useAdoptedStyleSheet';

export type LoadingBarVariant = 'default' | 'warning' | 'error';

export type LoadingBarSize = 'small' | 'medium' | 'large';

export interface LoadingBarProps {
    /**
     * Whether the bar is animating. When false the line is hidden but its height
     * is preserved, so toggling it doesn't shift the layout underneath.
     */
    active?: boolean;
    /**
     * Semantic colour role. Colours come from theme tokens by design — there is no
     * free-form colour prop, so a consumer can't paint the bar off-palette. To
     * restyle a variant, override its tokens via `applyTheme` / `applyCustomTheme`.
     */
    variant?: LoadingBarVariant;
    /** Line thickness. Fixed steps rather than a free px value, to keep bars consistent. */
    size?: LoadingBarSize;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    className?: string;
    style?: React.CSSProperties;
}

// Indeterminate: a full-width pale line with the variant colour sweeping horizontally
// across it, for work of unknown duration (e.g. a background refetch). Distinct from
// Progress, which fills to a known value.
const KEYFRAME_NAME = 'malachite-loading-bar-sweep';
const BAND_CLASS = 'malachite-loading-bar-band';
// The band's colours vary per variant, but its stylesheet rules are shared. Pass the two
// resolved colours in as custom properties so the rules below can build both the
// animated gradient and the reduced-motion fallback. Component-internal, NOT theme
// tokens — consumers never set these.
const TRACK_VAR = '--malachite-loading-bar-track';
const BAND_VAR = '--malachite-loading-bar-band';

// Width of the travelling band, as a fraction of the bar.
const BAND_WIDTH_PERCENT = 40;
// Translate the band from fully off the left edge to fully off the right edge.
// translateX percentages resolve against the BAND's own width, so clearing the full bar
// means moving 100% / 0.4 = 250% of the band.
//
// This deliberately does NOT animate `background-position` on a single oversized
// gradient. A percentage background-position resolves against (container - image), so at
// `background-size: 300%` that is -2W: the image travels 4W while the highlight is only
// inside the visible window for a quarter of the cycle, leaving the bar looking static
// for the rest and then snapping back. Translating a real element sweeps uniformly and
// stays on the compositor.
//
// The band is styled here rather than inline because a `prefers-reduced-motion` media
// query lives in a stylesheet and cannot override inline styles.
const KEYFRAMES_CSS = `
@keyframes ${KEYFRAME_NAME} {
    from { transform: translateX(-100%); }
    to { transform: translateX(${100 / (BAND_WIDTH_PERCENT / 100)}%); }
}
.${BAND_CLASS} {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${BAND_WIDTH_PERCENT}%;
    /* Fading to the track colour at both ends (rather than to transparent) means the
       band has no visible edge, and avoids the muddy interpolation through
       transparent black. */
    background-image: linear-gradient(90deg, var(${TRACK_VAR}) 0%, var(${BAND_VAR}) 50%, var(${TRACK_VAR}) 100%);
    animation: ${KEYFRAME_NAME} 1.3s linear infinite;
}
/* Looping motion can cause genuine nausea and dizziness for users with vestibular
   disorders, and an indeterminate bar loops with nothing to look away from. Stop the
   sweep but keep the bar obviously busy: a solid full-width line. Freezing the 40% band
   in place instead would read as "40% complete", which this component never means. */
@media (prefers-reduced-motion: reduce) {
    .${BAND_CLASS} {
        animation: none;
        width: 100%;
        background-image: none;
        background-color: var(${BAND_VAR});
    }
}`;
// Kept local to the component for now — these are line weights, not a shared scale,
// so they don't warrant a theme token group until something else needs them.
const SIZE_HEIGHTS: Record<LoadingBarSize, number> = {
    small: 2,
    medium: 3,
    large: 6,
};

// One base token per semantic role. Inline fallbacks cover the window before the theme
// is applied / outside a theme root.
const VARIANT_BASE: Record<LoadingBarVariant, string> = {
    default: 'var(--malachite-accent-background, rgb(0, 175, 102))',
    warning: 'var(--malachite-warning-background, rgb(241, 139, 10))',
    error: 'var(--malachite-error-background, rgb(197, 63, 63))',
};

// The pale track colour is DERIVED from the variant's base by mixing toward white,
// rather than being a second hand-picked token per variant. Hand-picking ships
// invisible sweeps: the status *text* tokens sit only ~5-15 perceived luminance from
// their own background colour, so a warning/error bar barely appeared to move at all.
// Deriving keeps every variant at a comparable delta and stays on-palette for any
// variant added later.
//
// Lower share of base = paler track = higher contrast against the band. This is the one
// knob to turn if the sweep reads too subtle or too washed out. At 35% the
// perceived-luminance gap is ~91 (default), ~65 (warning), ~99 (error).
const TRACK_BASE_SHARE_PERCENT = 35;
const trackTintFor = (base: string): string =>
    `color-mix(in srgb, ${base} ${TRACK_BASE_SHARE_PERCENT}%, white)`;

const LoadingBar: React.FC<LoadingBarProps> = ({
    active = true,
    variant = 'default',
    size = 'medium',
    'aria-label': ariaLabel = 'Loading',
    'aria-labelledby': ariaLabelledBy,
    className = '',
    style = {},
}) => {
    const ref = useRef<HTMLDivElement>(null);
    useAdoptedStyleSheet(ref, KEYFRAMES_CSS, 'malachite-loading-bar-keyframes');

    // Pale track with the saturated colour sweeping over it. Reads noticeably cleaner
    // than the inverse (saturated track, pale band), which looks washed out at these
    // line weights.
    const band = VARIANT_BASE[variant];
    const track = trackTintFor(band);

    return (
        <div
            ref={ref}
            // Idle is a height-preserving placeholder, not a live indicator — take it
            // out of the a11y tree entirely so a hidden bar is never announced as
            // "Loading". Mirrors Skeleton's aria-busy + aria-hidden pairing.
            role={active ? 'progressbar' : undefined}
            aria-busy={active || undefined}
            aria-hidden={active ? undefined : true}
            aria-label={active ? ariaLabel : undefined}
            aria-labelledby={active ? ariaLabelledBy : undefined}
            className={className}
            // Cast because CSSProperties has no shape for custom properties.
            style={
                {
                    position: 'relative',
                    overflow: 'hidden',
                    width: '100%',
                    height: `${SIZE_HEIGHTS[size]}px`,
                    backgroundColor: track,
                    // Hidden but height-preserving when idle, so toggling the bar
                    // never nudges the content below it. The opacity transition lets
                    // a consumer fade it out when work finishes rather than cutting.
                    opacity: active ? 1 : 0,
                    transition: 'opacity 250ms ease',
                    [TRACK_VAR]: track,
                    [BAND_VAR]: band,
                    ...style,
                } as React.CSSProperties
            }
        >
            {/* Mounted only while active, so an idle bar keeps no animation running. */}
            {active && <div className={BAND_CLASS} />}
        </div>
    );
};

export default LoadingBar;
export { LoadingBar };
